import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Chatbot from "@/components/Chatbot";
import SectionHeading from "@/components/SectionHeading";
import { ArrowUpRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1577717903315-1691ae25ab3f?auto=format&fit=crop&w=2400&q=85";

const MILESTONES = [
  { year: "2026", title: "The Founding Idea", body: "AZEFAC Properties was never designed to be a brokerage. It was crafted with care and purpose — quietly, brief by brief — for principals who had outgrown the public market and required a different kind of conversation. Luxury and quality. This is how it came to be." },
  { year: "2020", title: "First Mandate", body: "Our inaugural off-market mandate — a beachfront villa on Palm Jumeirah's outer frond — closed to a buyer never seen on the open market." },
  { year: "2022", title: "Branded Residences Desk", body: "We established direct allocations with Bulgari, Armani/Casa, Cavalli and Bugatti — bringing branded inventory to clients before public release." },
  { year: "2024", title: "Family Office Practice", body: "Expanded into residency, structuring, and onward exits — closing the loop on the full HNW relocation arc." },
  { year: "2026", title: "Maison Atelier Today", body: "Representing fewer than ninety principals at any given moment, with offices in DIFC and a global referral network across London, Singapore and Geneva." },
];

const NUMBERS = [
  { v: "90", l: "Principals Represented" },
  { v: "AED 4.2B", l: "Cumulative Mandates" },
  { v: "24mo", l: "Average Client Tenure" },
  { v: "100%", l: "Off-Market Sourcing" },
];

export default function AboutPage() {
  return (
    <main data-testid="about-page" className="bg-[#f5f0e8] text-[#1c1c1c] grain">
      <Navbar solid />

      {/* Hero */}
      <section className="pt-32 lg:pt-40 pb-16 max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-end">
          <div className="lg:col-span-7">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">
              The story of a<br />
              <em className="not-italic text-[#3d2b1f]">private house</em>.
            </h1>
            <span className="underline-label mt-7">About · AZEFAC Properties</span>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[#3d2b1f] text-base leading-relaxed">
              AZEFAC Properties was never designed to be a brokerage. It was built — quietly,
              brief by brief — for principals who had outgrown the public market and required a
              different kind of conversation. This is how it came to be.
            </p>
          </div>
        </div>
      </section>

      {/* Cinematic image */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 mb-24">
        <div className="img-zoom aspect-[16/9]">
          <img src={HERO_IMG} alt="Dubai Marina at twilight" className="w-full h-full object-cover" />
        </div>
      </section>

      {/* Origin essay */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 mb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-4">
            <SectionHeading title={<>How AZEFAC<br /><em className="not-italic text-[#3d2b1f]">came to life</em>.</>} subtitle="Origin" />
          </div>
          <div className="lg:col-span-8 space-y-6 text-[#3d2b1f] text-lg leading-relaxed">
            <p>
              AZEFAC Properties was founded in 2026, by the visionary Azefac Dieudonne
              Chambang — a UAE-based serial entrepreneur and investor with over a decade of
              experience in real estate, analytical thinking, financing and capital
              structuring. Azefac has leveraged his extensive expertise to create a powerhouse
              in the real estate industry.
            </p>
            <p>
              In just a few months, we had built direct relationships with the major
              branded-residence ateliers in Dubai. We had grown the desk to include residency,
              family-office structuring, and onward exit — recognising that a residence is
              rarely the only thing a principal needs from us.
            </p>
            <p>
              Today, AZEFAC represents fewer than ninety principals at any given moment. Our
              work is measured in tenure, not transactions — and the residences we curate
              quietly outperform the wider Dubai prime market by a factor we keep to ourselves.
            </p>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-[#ede5d5] py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <SectionHeading title={<>Eight years,<br /><em className="not-italic text-[#3d2b1f]">five chapters</em>.</>} subtitle="Timeline" />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
            {MILESTONES.map((m, i) => (
              <div key={`${m.year}-${i}`} data-testid={`milestone-${i}-${m.year}`} className="border-t border-[#1c1c1c]/15 pt-6">
                {i === 0 && (
                  <div className="font-display text-4xl text-[#c5a059]">{m.year}</div>
                )}
                <h3 className={`font-display text-2xl ${i === 0 ? "mt-3" : ""} text-[#1c1c1c] leading-tight`}>{m.title}</h3>
                <p className="mt-4 text-sm text-[#3d2b1f] leading-relaxed">{m.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Numbers */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <SectionHeading title="By the numbers." subtitle="The House at a glance" />
        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 border-t border-[#1c1c1c]/10">
          {NUMBERS.map((n, i) => (
            <div key={n.l} className={`p-10 border-b border-[#1c1c1c]/10 ${i < 3 ? "lg:border-r" : ""}`}>
              <div className="font-display text-5xl lg:text-6xl text-[#1c1c1c] leading-none">{n.v}</div>
              <span className="underline-label mt-5">{n.l}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CTA to CEO Message */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24 lg:pb-36">
        <div className="border-t border-[#1c1c1c]/10 pt-16 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div>
            <h2 className="font-display text-3xl lg:text-5xl text-[#1c1c1c] leading-tight max-w-2xl">
              Read the founder&apos;s message<br />on the vision ahead.
            </h2>
          </div>
          <Link
            to="/vision"
            data-testid="about-cta-ceo"
            className="inline-flex items-center gap-3 px-7 py-4 bg-[#c5a059] text-[#1c1c1c] uppercase text-[11px] tracking-[0.32em] hover:bg-[#1c1c1c] hover:text-[#f5f0e8] transition-colors duration-500"
          >
            CEO Message <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>

      <Footer />
      <Chatbot />
    </main>
  );
}
